import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { RouterLink, Router, ActivatedRoute, Params } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../../services/api.service';
import { CreativeAnalysisService } from '../../../services/creative-analysis.service';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import { FormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CreativeAnalysisImage, CreativeAnalysisVideo, CreativeAnalysisCarousel, CreativeStyle, CreativeFormat, CarouselCreative } from '../../../interfaces/creative-analysis';
import { environment } from '../../../../environments/environment';
import { NgxJsonViewerModule } from 'ngx-json-viewer';

import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-creative-analysis-batch',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    NgbTooltipModule,
    NgxJsonViewerModule
  ],
  templateUrl: './creative-analysis-batch.component.html',
  styleUrl: './creative-analysis-batch.component.css'
})
export class CreativeAnalysisBatchComponent implements OnInit {
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  creativeAnalysisService: CreativeAnalysisService = inject(CreativeAnalysisService);
  apiService: ApiService = inject(ApiService);
  toastrService: ToastrService = inject(ToastrService);

  // User Authentication
  authenticatedUser = this.authService.authenticatedUser;

  // Component-specific variables
  accountId: string = '';

  pageTitle = 'Creatives';

  reviewMode: 'img' | 'vid' | 'car' | null = null;

  environment = environment;

  requestedCreativeIds: string = '';
  maxFilesPerBatch = 20; // Maximum number of files allowed in a batch

  numberOfCreatives: number | null = null;
  showUploadCreatives = true;

  selectedModel: 'openai' | 'gemini' = 'gemini';
  dropdownModelOpen = false;

  asyncDropFiles: {name: string, file: File, status: 'pending' | 'uploading...' | 'uploaded' | 'error'}[] = [];
  showAsyncDrop = true;
  maxFilesAsyncDrop = 100; // Maximum number of files allowed in a AsyncDrop batch

  reviewFormat: 'img' | 'vid' | 'car' = 'img';
  reviewNextPageToken: string = '';
  reviewShowNextPageLink = false;

  // Create a list of creatives to hold the creative analysis data (images and videos)
  creatives: (CreativeAnalysisImage | CreativeAnalysisVideo | CreativeAnalysisCarousel)[] = [];
  loadingCreatives = false;
  carousel: {id: string, classified: boolean, processIndicator: string, files: {id: string, order: number, file: File, status: 'pending' | 'classifying' | 'done' | 'error'}[]} | null = null;

  creativeStyles: CreativeStyle[] = [];
  selectCreativeStyles: { [key: string]: string[] } = {};
  creativeFormats: CreativeFormat[] = [
    {id: 'high-fidelity', name: 'High Fidelity', forImage: true, forVideo: true},
    {id: 'low-fidelity', name: 'Low Fidelity', forImage: true, forVideo: true},
    {id: 'motion-graphics-animated', name: 'Motion Graphics/Animated', forImage: false, forVideo: true},
    {id: 'gif', name: 'GIF', forImage: false, forVideo: true},
    {id: 'na', name: 'N/A', forImage: true, forVideo: true}
  ];
  selectCreativeFormat: { [key: string]: string[] } = {};
  personFeatures = ['Yes', 'No'];
  selectPersonFeature: { [key: string]: string[] } = {};
  ugc = ['Yes', 'No'];
  selectUgc: { [key: string]: string[] } = {};
  marketAwareness: string[] = ['Unaware', 'Problem Aware' , 'Solution Aware', 'Product Aware', 'Most Aware'];
  selectMarketAwareness: { [key: string]: string[] } = {};
  gender = ['Female', 'Male', 'Unisex', 'N/A'];
  selectGender: { [key: string]: string[] } = {};
  
  updateFieldValue: {
    [creativeId: string]: {
      [fieldName: string]: {
        name: string;
        showButton: boolean;
        showLoading: boolean;
        updateStatus: 'saved' | 'error' | null;
      };
    };
  } = {};

  carouselsCreatives: { [creativeId: string]: CarouselCreative[] } = {};

  // A map to hold the polling state and subscription for each creative
  private pollingMap: { [creativeId: string]: { subscription: Subscription, isActive: boolean } } = {};

  ngOnInit(): void {    
    // Check that user is authenticated (should be the case if this path is protected with AuthGuardService in app.routes.ts)
    this.authService.checkUserSignedIn().then(checkSign => {
      if (!checkSign) {
        // User is NOT signed in => Call AuthService.signOut() to redirect to Sign in page
        this.authService.signOut();
      } else {
        // User is signed in
        // Get Ad Account ID from URL params
        this.activatedRoute.params.subscribe((params: Params) => {
          this.accountId = params['id'];          

          // Get the query parameters from the URL
          this.activatedRoute.queryParamMap.subscribe(params => {
            if(params.get('review')) {
              const reviewParam = params.get('review');
              if (reviewParam === 'img' || reviewParam === 'vid' || reviewParam === 'car') {
                this.reviewMode = reviewParam;
                this.reviewCreatives(this.reviewMode);
              } else {
                this.reviewMode = null;
              }
            } else {
              this.pageTitle = "Add Creatives";
            }
          });

          console.log(`Loading Ad Account#: ${this.accountId}`);
          // Check if the user has access to the Ad Accounts section & this specific ad account
          this.authService.checkUserHasAccessToAdAccounts().then(adAccounts => {
            if(adAccounts === null || !adAccounts.some(adAccount => adAccount.id === this.accountId)) {
              // User does not have access to the account (or the user has no ad accounts at all)
              console.log("User does not have access to this ad account");
              this.toastrService.error("You do not have access to this Ad Account", "Access Denied");
              this.router.navigate(['/']);
            } else {
              // User has access to the account
              console.log("User has access to this ad account");

            }        
          });

          // Load creative styles
          this.getCreativeStyles();
        });
      }  
    });
  }

  // Add this new trackBy function for ngFor loops over arrays of primitive types
  trackByIndex(index: number, item: any): number {
    return index;
  }  

  getCreatives(creativeIds: string[]): void {
    try {
      creativeIds.forEach((creativeId: string) => {
        console.log('forEach creative:', creativeId);
        const creativeIndex = this.creatives.findIndex(c => c.id === creativeId);
        this.creatives[creativeIndex].loading = true;
      });

      this.creativeAnalysisService.getCreatives(this.accountId, creativeIds)
      .then(creatives => {
        console.log('getCreatives:', creatives);
        this.loadingCreatives = false;
        // For each creative, update the thumbnail
        creatives.forEach((creative: any) => {
          console.log('[getCreatives] Loaded creative:', creative);
          const creativeIndex = this.creatives.findIndex(c => c.id === creative.id);
          this.creatives[creativeIndex].thumbnail = creative.thumbnail;
          this.creatives[creativeIndex].url = creative.url;
          this.creatives[creativeIndex].loading = false;
          this.creatives[creativeIndex].analysing = false;
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].classified = creative.classified;
          this.creatives[creativeIndex].classificationId = creative.classification;
          if(creative.job && creative.job.result && creative.job.result.classification) {
            this.creatives[creativeIndex].mainAngle = creative.job.result.classification.main_angle;
            this.creatives[creativeIndex].mainAngleReasons = creative.job.result.classification.main_angle_reasons;
            this.creatives[creativeIndex].mainAngleAlternatives = creative.job.result.classification.main_angle_alternatives;
            this.creatives[creativeIndex].creativeStyle = creative.job.result.classification.creative_style;
            this.creatives[creativeIndex].creativeFormat = creative.job.result.classification.creative_format;
            this.creatives[creativeIndex].creativeStyleAlternatives = creative.job.result.classification.creative_style_alternatives;
            this.creatives[creativeIndex].creativeStyleReasons = creative.job.result.classification.creative_style_reasons;
            this.creatives[creativeIndex].targetMarket = creative.job.result.classification.target_market.description;
            this.creatives[creativeIndex].targetMarketDescription = creative.job.result.classification.target_market.description;
            this.creatives[creativeIndex].targetMarketReasons = creative.job.result.classification.target_market.reasons;
            this.creatives[creativeIndex].marketAwareness = creative.job.result.classification.market_awareness;
            this.creatives[creativeIndex].marketAwarenessReasons = creative.job.result.classification.market_awareness_reason
            this.creatives[creativeIndex].ugc = creative.job.result.classification.ugc;
            this.creatives[creativeIndex].discounts = creative.job.result.classification.discounts;
            this.creatives[creativeIndex].discount = creative.job.result.classification.discounts.discount;
            this.creatives[creativeIndex].discountCode = creative.job.result.classification.discounts.discount_code;
            this.creatives[creativeIndex].headline = creative.job.result.classification.headline;
            this.creatives[creativeIndex].personFeatureVisible = creative.job.result.classification.person_feature.visible;
            this.creatives[creativeIndex].personFeatureDescription = creative.job.result.classification.person_feature.description;
            this.creatives[creativeIndex].callToAction = creative.job.result.classification.call_to_action;
            this.creatives[creativeIndex].model = creative.job.result.classification.model;
            this.creatives[creativeIndex].detectedTexts = creative.job.result.classification.detected_texts;
            this.creatives[creativeIndex].usageTokensTotal = creative.job.result.classification.usage_tokens_total;
            this.creatives[creativeIndex].prompt = creative.job.result.classification.prompt;

            // Ensure usps is an array and pad it consistently
            let loadedUsps: string[] = [];
            if (Array.isArray(creative.job.result.classification.usps)) { // or 'creative.usps' in reviewCreatives
              loadedUsps = [...creative.job.result.classification.usps]; // Create a new array to ensure Angular detects change
            }
            // Pad the array to 10 elements
            while (loadedUsps.length < 10) {
              loadedUsps.push('');
            }
            this.creatives[creativeIndex].usps = loadedUsps;
          }
        });        
      })
      .catch(error => {
        console.error('Error fetching creative', error);
        this.loadingCreatives = false;
        if(error == 'CreativeNotFound') {
          this.toastrService.warning('Creative not found. Upload a file to get started.');
        } else {
          this.toastrService.error(`Error fetching creative [${error}]`);
        }
      });
    } catch (error) {
      console.error('Error fetching creative', error);
      this.loadingCreatives = false;
    }
  }

  updateRequestedCreativeIds(): void {
    if(this.requestedCreativeIds.length > 0) {
      this.creatives = this.requestedCreativeIds.split(',').map((id: string) => {
        return {
          id: id,
          thumbnail: '/assets/images/pix.gif',
          usps: Array(10).fill('') // Initialize usps as an array of 10 empty strings
        };
      });
      this.numberOfCreatives = this.creatives.length;

      if(this.numberOfCreatives !== null && this.numberOfCreatives > 0) {
        this.showUploadCreatives = false;
        this.getCreatives(this.creatives.map(creative => creative.id));
      }
    }
  }

  classifyCreative(creativeId: string) {
    // Get the index of the creative in the creatives array based on the creativeId
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    this.creatives[creativeIndex].analysing = true;
    this.creatives[creativeIndex].analysisStatus = 'Processing...';
    this.creatives[creativeIndex].error = '';
    console.log('Classifying creative...', creativeId, creativeIndex);

    this.creativeAnalysisService.requestAnalysis(this.accountId, creativeId, this.selectedModel)
    .then((requestAnalysisResponse) => {
      console.log('Creative analysis requested', creativeId, requestAnalysisResponse);
      const jobID = requestAnalysisResponse.job;
      // this.toastrService.info('Creative analysis requested. Job ID: ' + jobID);
      this.creatives[creativeIndex].analysing = false;
      this.creatives[creativeIndex].analysisStatus = '';

      // Poll for job status
      console.log('Starting polling for job status...', creativeId, creativeId);
      this.startPolling(creativeId);

    }).catch(error => {
      console.error('Error getting S3 Upload URL', creativeId, error);
      this.creatives[creativeIndex].analysing = false;
      this.creatives[creativeIndex].analysisStatus = '';
    });
  }


  startPolling(creativeId: string): void {
    const pollingInterval = 2000; // 2 seconds
    const maxPollingTime = 180000; // 180 seconds

    // If there's already a polling process for this ID, stop it first
    if (this.pollingMap[creativeId]) {
      this.stopPolling(creativeId);
    }

    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    this.creatives[creativeIndex].analysing = true;
    this.creatives[creativeIndex].analysisStatus = 'Processing...';

    // Setup polling
    this.pollingMap[creativeId] = {
      subscription: new Subscription(),
      isActive: true
    }; // Store the polling info

    this.pollingMap[creativeId].subscription = interval(pollingInterval).pipe(
      switchMap(() => this.creativeAnalysisService.checkJobStatus(this.accountId, creativeId)),
      takeWhile(() => this.pollingMap[creativeId].isActive)  // Check the isActive flag specific to this creativeId
    ).subscribe(response => {
      console.log('Polling response', creativeId, response.status);      
      if(response.status === 'completed') {
        console.log('Creative analysis completed', creativeId);
        this.loadCreativeAnalysis(creativeId);
        this.creatives[creativeIndex].analysing = false;
        this.creatives[creativeIndex].analysisStatus = '';
        if(creativeId.startsWith('CAR')) {
          console.log('Carousel being analysed:', creativeId, this.carousel, this.getCarouselStatus(), this.getCarouselFiles());
          // Change the status of this file in the carousel to done
          // Get the file name from the carousel object
          const fileName = this.carousel?.files.find(file => file.order === parseInt(creativeId.split('_')[1], 10));
          console.log('Carousel File name:', fileName);
          if(fileName) {
            // Get the digits after the last underscore and before the file extension
            const match = fileName.file.name.match(/_(\d{1,2})(?=\.[^/.]+$)/);
            const lastDigits = match ? match[1] : null;
            const positionInCAR = parseInt(lastDigits ?? '', 10);
            console.log('Position in CAR:', positionInCAR);

            if (!isNaN(positionInCAR) && this.carousel?.files[positionInCAR - 1]) {
              this.carousel.files[positionInCAR - 1].status = 'done';
              console.log('Carousel being analysed:', creativeId, this.getCarouselStatus(), this.getCarouselFiles());

              // Check if all carousel files are done
              const allDone = this.carousel.files.every(file => file.status === 'done');
              if (allDone) {
                console.log('All carousel files are done. Requesting overall carousel classification.', creativeId, this.carousel, this.getCarouselStatus(), this.getCarouselFiles());
                this.finaliseCarouselAnalysis();
              }
            } else {
              console.warn('Invalid or missing position in carousel filename:', fileName.file.name);
            }
          }  
          // Update the process indicator        
          const totalItems = this.carousel?.files || [];
          const classifiedItems = totalItems.filter(item => item.status === 'done').length;        
          this.carousel!.processIndicator = `${classifiedItems}/${totalItems.length}`;                   
        }
        this.stopPolling(creativeId);        
      } else if(response.status === 'failed' || response.status === 'error') {
        console.error('Creative analysis failed', response);
        this.creatives[creativeIndex].analysing = false;
        this.creatives[creativeIndex].analysisStatus = '';
        this.creatives[creativeIndex].error = response.error;
        this.toastrService.error(response.error, `Creative analysis failed for ${creativeId}`);
        this.stopPolling(creativeId);
      }
    }, error => {
      console.error('Polling error:', error);
      this.creatives[creativeIndex].analysing = false;
      this.creatives[creativeIndex].analysisStatus = '';
      this.creatives[creativeIndex].error = error.message ?? error;
      this.toastrService.error(error.message ?? error, `Creative analysis failed for ${creativeId}`);
      this.stopPolling(creativeId);
    });

    // Stop polling after maxPollingTime
    setTimeout(() => {
      console.log('Polling timed out', creativeId);
      if(this.pollingMap[creativeId].isActive) {
        console.error(`Polling timed out after ${maxPollingTime / 1000} seconds`);
        this.creatives[creativeIndex].analysing = false;
        this.creatives[creativeIndex].analysisStatus = '';
        this.stopPolling(creativeId);
      }
    }, maxPollingTime);
  }

  stopPolling(creativeId: string) {
    if (this.pollingMap[creativeId] && this.pollingMap[creativeId].isActive) {
      this.pollingMap[creativeId].isActive = false;
      if (this.pollingMap[creativeId].subscription) {
        this.pollingMap[creativeId].subscription.unsubscribe();
      }
      console.log(`Polling stopped for ${creativeId}`);
      const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
      this.creatives[creativeIndex].analysisStatus = '';
    }
  }

  loadCreativeAnalysis(creativeId: string): void {
    console.log('loadCreativeAnalysis', creativeId);

    // Get the index of the creative in the creatives array based on the creativeId
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    this.creatives[creativeIndex].analysing = true;
    this.creatives[creativeIndex].analysisStatus = 'Loading...';

    if(creativeId.startsWith('IMG')) {
      this.creativeAnalysisService.getCreativeAnalysisImage(this.accountId, creativeId).then(analysis => {
        console.log('Creative analysis IMG:', analysis);
        // Get the index of the creative in the creatives array based on the creativeId
        const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
        if(creativeIndex !== -1) {
          this.creatives[creativeIndex].analysing = false;
          this.creatives[creativeIndex].classified = analysis.classified;
          this.creatives[creativeIndex].classificationId = analysis.classificationId;
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].needsQa = analysis.needsQa;
          this.creatives[creativeIndex].needsExpertReview = analysis.needsExpertReview;
          this.creatives[creativeIndex].needsExpertReviewReason = analysis.needsExpertReviewReason;
          this.creatives[creativeIndex].mainAngle = analysis.mainAngle;
          this.creatives[creativeIndex].creativeStyle = analysis.creativeStyle;
          this.creatives[creativeIndex].creativeFormat = analysis.creativeFormat;
          this.creatives[creativeIndex].targetMarket = analysis.targetMarket;
          this.creatives[creativeIndex].marketAwareness = analysis.marketAwareness;
          this.creatives[creativeIndex].discounts = analysis.discounts;
          this.creatives[creativeIndex].ugc = analysis.ugc;
          this.creatives[creativeIndex].headline = analysis.headline;
          this.creatives[creativeIndex].personFeature = analysis.personFeature;
          this.creatives[creativeIndex].callToAction = analysis.callToAction;
          this.creatives[creativeIndex].mainAngleReasons = analysis.mainAngleReasons;
          this.creatives[creativeIndex].mainAngleAlternatives = analysis.mainAngleAlternatives;
          this.creatives[creativeIndex].creativeStyleAlternatives = analysis.creativeStyleAlternatives;
          this.creatives[creativeIndex].creativeStyleReasons = analysis.creativeStyleReasons;
          this.creatives[creativeIndex].targetMarketAgeCategory = analysis.targetMarketAgeCategory;
          this.creatives[creativeIndex].targetMarketGender = analysis.targetMarketGender;
          this.creatives[creativeIndex].targetMarketDescription = analysis.targetMarketDescription;
          this.creatives[creativeIndex].targetMarketReasons = analysis.targetMarketReasons;
          this.creatives[creativeIndex].marketAwarenessReasons = analysis.marketAwarenessReasons;
          this.creatives[creativeIndex].discount = analysis.discount;
          this.creatives[creativeIndex].discountCode = analysis.discountCode;
          this.creatives[creativeIndex].personFeatureVisible = analysis.personFeatureVisible;
          this.creatives[creativeIndex].personFeatureDescription = analysis.personFeatureDescription;
          this.creatives[creativeIndex].model = analysis.model;
          this.creatives[creativeIndex].detectedTexts = analysis.detectedTexts;
          this.creatives[creativeIndex].usageTokensTotal = analysis.usageTokensTotal;
          this.creatives[creativeIndex].prompt = analysis.prompt;
          this.creatives[creativeIndex].reasons = analysis.reasons;
          this.creatives[creativeIndex].rawOutput = analysis.rawOutput;
          this.creatives[creativeIndex].showDebug = false;

          let loadedUsps: string[] = [];
          if (Array.isArray(analysis.usps)) {
            loadedUsps = [...analysis.usps]; // Create a new array to ensure Angular detects change
          }
          // Pad the array to 10 elements
          while (loadedUsps.length < 10) {
            loadedUsps.push('');
          }
          this.creatives[creativeIndex].usps = loadedUsps;

          // Build the select lists
          this.buildSelectCreativeStyles(creativeId, this.creatives[creativeIndex].creativeStyle || '');
          this.buildSelectCreativeFormat(creativeId);
          this.buildSelectPersonFeature(creativeId);
          this.buildSelectUgc(creativeId);
          this.buildSelectMarketAwareness(creativeId);
          this.buildSelectGender(creativeId);
        } else {
          console.error('Creative not found in creatives array:', creativeId);
        }
      }).catch(error => {
        console.error('Error fetching creative analysis', error);
        const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
        if(creativeIndex !== -1) {
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].analysing = false;
        }
        this.toastrService.error('Error fetching creative analysis');
      });
    } else if(creativeId.startsWith('VID')) {
      this.creativeAnalysisService.getCreativeAnalysisVideo(this.accountId, creativeId).then(analysis => {
        console.log('Creative analysis VID:', analysis);
        // Get the index of the creative in the creatives array based on the creativeId
        const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
        if(creativeIndex !== -1) {
          this.creatives[creativeIndex].analysing = false;
          this.creatives[creativeIndex].classified = analysis.classified;
          this.creatives[creativeIndex].classificationId = analysis.classificationId;
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].needsQa = analysis.needsQa;
          this.creatives[creativeIndex].needsExpertReview = analysis.needsExpertReview;
          this.creatives[creativeIndex].needsExpertReviewReason = analysis.needsExpertReviewReason;
          this.creatives[creativeIndex].mainAngle = analysis.mainAngle;
          this.creatives[creativeIndex].creativeStyle = analysis.creativeStyle;
          this.creatives[creativeIndex].creativeFormat = analysis.creativeFormat;
          this.creatives[creativeIndex].targetMarket = analysis.targetMarket;
          this.creatives[creativeIndex].marketAwareness = analysis.marketAwareness;
          this.creatives[creativeIndex].discounts = analysis.discounts;
          this.creatives[creativeIndex].ugc = analysis.ugc;
          this.creatives[creativeIndex].audioHook = analysis.audioHook;        
          this.creatives[creativeIndex].visualHook = analysis.visualHook;
          this.creatives[creativeIndex].textHook = analysis.textHook;
          this.creatives[creativeIndex].ageGroup = analysis.ageGroup;
          this.creatives[creativeIndex].gender = analysis.gender;
          this.creatives[creativeIndex].length = analysis.length;
          this.creatives[creativeIndex].audioClose = analysis.audioClose;
          this.creatives[creativeIndex].visualClose = analysis.visualClose;
          this.creatives[creativeIndex].textClose = analysis.textClose;
          this.creatives[creativeIndex].mainAngleAlternatives = analysis.mainAngleAlternatives;
          this.creatives[creativeIndex].creativeStyleAlternatives = analysis.creativeStyleAlternatives;
          this.creatives[creativeIndex].creativeStyleReasons = analysis.creativeStyleReasons;
          this.creatives[creativeIndex].targetMarketAgeCategory = analysis.targetMarketAgeCategory;
          this.creatives[creativeIndex].targetMarketGender = analysis.targetMarketGender;
          this.creatives[creativeIndex].targetMarketDescription = analysis.targetMarketDescription;
          this.creatives[creativeIndex].targetMarketReasons = analysis.targetMarketReasons;
          this.creatives[creativeIndex].marketAwarenessReasons = analysis.marketAwarenessReasons;
          this.creatives[creativeIndex].discount = analysis.discount;
          this.creatives[creativeIndex].discountCode = analysis.discountCode;
          this.creatives[creativeIndex].model = analysis.model;
          this.creatives[creativeIndex].detectedTexts = analysis.detectedTexts;
          this.creatives[creativeIndex].usageTokensTotal = analysis.usageTokensTotal;
          this.creatives[creativeIndex].prompt = analysis.prompt;
          this.creatives[creativeIndex].reasons = analysis.reasons;
          this.creatives[creativeIndex].rawOutput = analysis.rawOutput;
          this.creatives[creativeIndex].showDebug = false;
          
          let loadedUsps: string[] = [];
          if (Array.isArray(analysis.usps)) {
            loadedUsps = [...analysis.usps]; // Create a new array to ensure Angular detects change
          }
          // Pad the array to 10 elements
          while (loadedUsps.length < 10) {
            loadedUsps.push('');
          }
          this.creatives[creativeIndex].usps = loadedUsps;

          // Build the select lists
          this.buildSelectCreativeStyles(creativeId, this.creatives[creativeIndex].creativeStyle || '');
          this.buildSelectCreativeFormat(creativeId);
          this.buildSelectPersonFeature(creativeId);
          this.buildSelectUgc(creativeId);
          this.buildSelectMarketAwareness(creativeId);
          this.buildSelectGender(creativeId);          
        } else {
          console.error('Creative not found in creatives array:', creativeId);
        }
      }).catch(error => {
        console.error('Error fetching creative analysis', error);
        const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
        if(creativeIndex !== -1) {
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].analysing = false;
        }
        this.toastrService.error('Error fetching creative analysis');
      });

    } else if(creativeId.startsWith('CAR')) {
      this.creativeAnalysisService.getCreativeAnalysisCarousel(this.accountId, creativeId).then(analysis => {
        console.log('Creative analysis CAR:', analysis);
        // Get the index of the creative in the creatives array based on the creativeId
        const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
        if(creativeIndex !== -1) {
          this.creatives[creativeIndex].analysing = false;
          this.creatives[creativeIndex].classified = analysis.classified;
          this.creatives[creativeIndex].classificationId = analysis.classificationId;
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].needsQa = analysis.needsQa;
          this.creatives[creativeIndex].needsExpertReview = analysis.needsExpertReview;
          this.creatives[creativeIndex].needsExpertReviewReason = analysis.needsExpertReviewReason;
          this.creatives[creativeIndex].mainAngle = analysis.mainAngle;
          this.creatives[creativeIndex].creativeStyle = analysis.creativeStyle;
          this.creatives[creativeIndex].creativeFormat = analysis.creativeFormat;
          this.creatives[creativeIndex].targetMarket = analysis.targetMarket;
          this.creatives[creativeIndex].marketAwareness = analysis.marketAwareness;
          this.creatives[creativeIndex].discounts = analysis.discounts;         
          this.creatives[creativeIndex].ugc = analysis.ugc;
          this.creatives[creativeIndex].numberOfTiles = analysis.numberOfTiles;
          this.creatives[creativeIndex].mainAngleReasons = analysis.mainAngleReasons;
          this.creatives[creativeIndex].mainAngleAlternatives = analysis.mainAngleAlternatives;
          this.creatives[creativeIndex].creativeStyleAlternatives = analysis.creativeStyleAlternatives;
          this.creatives[creativeIndex].creativeStyleReasons = analysis.creativeStyleReasons;
          this.creatives[creativeIndex].targetMarketAgeCategory = analysis.targetMarketAgeCategory;
          this.creatives[creativeIndex].targetMarketGender = analysis.targetMarketGender;
          this.creatives[creativeIndex].targetMarketDescription = analysis.targetMarketDescription;
          this.creatives[creativeIndex].targetMarketReasons = analysis.targetMarketReasons;
          this.creatives[creativeIndex].marketAwarenessReasons = analysis.marketAwarenessReasons;
          this.creatives[creativeIndex].discount = analysis.discount;
          this.creatives[creativeIndex].discountCode = analysis.discountCode;
          this.creatives[creativeIndex].model = analysis.model;
          this.creatives[creativeIndex].detectedTexts = analysis.detectedTexts;
          this.creatives[creativeIndex].usageTokensTotal = analysis.usageTokensTotal;
          this.creatives[creativeIndex].prompt = analysis.prompt;
          this.creatives[creativeIndex].reasons = analysis.reasons;
          this.creatives[creativeIndex].rawOutput = analysis.rawOutput;
          this.creatives[creativeIndex].showDebug = false;
          
          let loadedUsps: string[] = [];
          if (Array.isArray(analysis.usps)) {
            loadedUsps = [...analysis.usps]; // Create a new array to ensure Angular detects change
          }
          // Pad the array to 10 elements
          while (loadedUsps.length < 10) {
            loadedUsps.push('');
          }
          this.creatives[creativeIndex].usps = loadedUsps;

          // Build the select lists
          this.buildSelectCreativeStyles(creativeId, this.creatives[creativeIndex].creativeStyle || '');
          this.buildSelectCreativeFormat(creativeId);
          this.buildSelectPersonFeature(creativeId);
          this.buildSelectUgc(creativeId);
          this.buildSelectMarketAwareness(creativeId);
          this.buildSelectGender(creativeId);

          // Get the carousel creatives
          if(creativeId.length == 6) this.getCarouselCreative(creativeId);
        } else {
          console.error('Creative not found in creatives array:', creativeId);
        }
      }).catch(error => {
        console.error('Error fetching creative analysis', error);
        const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
        if(creativeIndex !== -1) {
          this.creatives[creativeIndex].analysisStatus = '';
          this.creatives[creativeIndex].analysing = false;
        }
        this.toastrService.error('Error fetching creative analysis');
      });

    }

  }

  requestAnalysis(creativeId: string): void {
    this.creativeAnalysisService.requestAnalysis(this.accountId, creativeId, this.selectedModel).then(response => {
      console.log('Creative analysis requested', response);
      // this.jobID = response.job;
      this.startPolling(creativeId);
    }).catch(error => {
      console.error('Error requesting creative analysis', error);
      this.toastrService.error('Error requesting creative analysis');
    });
  }

  finaliseCarouselAnalysis(): void {
    // Get the list of files' creative IDs
    const creativeIds = this.carousel?.files.map(file => file.id) || [];
    console.log('Finalising carousel analysis', this.carousel?.id, this.carousel?.files.length, creativeIds, this.carousel);

    // Send the request to classify the carousel
    this.creativeAnalysisService.requestCarouselAnalysis(this.accountId, this.carousel?.id ?? '', creativeIds).then(response => {
      this.carousel!.classified = true;
      console.log('Carousel final classification', response);

      // Combined usps logic into a single definition
      let uspsArray: string[];
      if (Array.isArray(response.classification.usps)) {
        uspsArray = [...response.classification.usps]; // Create a copy
      } else {
        uspsArray = [];
      }
      // Pad the usps array for the new creative
      while (uspsArray.length < 10) {
        uspsArray.push('');
      }
   
      // Use the response to create/update the carousel object
      const carouselCreative: CreativeAnalysisCarousel = {
        id: this.carousel!.id,
        thumbnail: response.thumbnail,
        url: response.thumbnail,
        needsQa: response.needs_qa,
        needsExpertReview: response.needs_expert_review,
        needsExpertReviewReason: response.needs_expert_review_reason,
        mainAngle: response.classification.main_angle,
        creativeStyle: response.classification.creative_style,
        creativeFormat: response.classification.creative_format,
        targetMarket: response.classification.target_market,
        marketAwareness: response.classification.market_awareness,
        discounts: response.classification.discounts,
        ugc: response.classification.ugc,
        numberOfTiles: response.classification.number_of_tiles,
        usps: uspsArray
      };
      // Check if the carousel object already exists in the creatives array
      const existingCarouselIndex = this.creatives.findIndex(c => c.id === carouselCreative.id);
      if(existingCarouselIndex !== -1) {
        // Carousel object already exists, update it
        this.creatives[existingCarouselIndex] = carouselCreative;
      } else {
        // Carousel object does not exist, add it to the beginning of the creatives array
        this.creatives.unshift(carouselCreative);
      }
      console.log('Carousel creative added', carouselCreative);
      // Build the select lists
      const creativeIndex = this.creatives.findIndex(creative => creative.id === this.carousel!.id);
      this.buildSelectCreativeStyles(this.carousel!.id, this.creatives[creativeIndex].creativeStyle || '');
      this.buildSelectCreativeFormat(this.carousel!.id);
      this.buildSelectPersonFeature(this.carousel!.id);
      this.buildSelectUgc(this.carousel!.id);
      this.buildSelectMarketAwareness(this.carousel!.id);
      this.buildSelectGender(this.carousel!.id);
    }).catch(error => {
      console.error('Error requesting carousel classification', error);
      this.toastrService.error('Error requesting carousel classification');
    });
  }

  onDragOver(event: DragEvent): void {
    // console.log('onDragOver', event);
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    // console.log('onDragLeave', event);
    event.preventDefault();
    event.stopPropagation();
  }

  onDroppedFiles(event: DragEvent): void {
    console.log('onDroppedFiles', event);
    event.preventDefault();
    event.stopPropagation();
    if(event.dataTransfer) {
      const files = event.dataTransfer.files;
      console.log('onDrop files:', files);
      if(files.length > this.maxFilesPerBatch) {
        this.toastrService.error(`Maximum ${this.maxFilesPerBatch} files allowed in a batch`);
        return;
      }
      this.handleFileInput(files);
    } else {
      this.toastrService.error('No files found in the drop');
    }    
  }

  onDropZoneClicked(): void {
    console.log('onDropZoneClicked');
    const fileInput = document.getElementById('filesSelector');
    if(fileInput) {
      fileInput.click();
    }
  }

  onFilesSelected(event: any): void {
    console.log('onFileChange', event);
    event.preventDefault();
    event.stopPropagation();
    if(event.target.files) {
      const files = event.target.files;
      console.log('onFilesSelected files:', files);
      if(files.length > this.maxFilesPerBatch) {
        this.toastrService.error(`Maximum ${this.maxFilesPerBatch} files allowed in a batch`);
        return;
      }
      this.handleFileInput(files);
    } else {
      this.toastrService.error('No files found in the selection');
    }
  }

  handleFileInput(files: FileList): void {
    if(!files || files.length === 0) {
      console.error('No files selected for upload');
      this.toastrService.error('No files selected for upload.');
      return;
    }
    if(files.length > this.maxFilesPerBatch) {
      this.toastrService.error(`Maximum ${this.maxFilesPerBatch} files allowed in a batch`);
      return;
    }
    this.showUploadCreatives = false;
    const fileArray = Array.from(files);
    fileArray.forEach((file, index) => {
      if(file) {
        const creativeId = file.name.substring(0, 6).toUpperCase();
        let addToCreatives = true;
        let addToCarousel = true;
        console.log('handleFileInput:', creativeId, file);

        if(creativeId.startsWith('CAR')) {
          // This is a carousel file          
          const match = file.name.match(/_(\d+)\.[a-z0-9]+$/i); // Extract digit(s) after last underscore before extension
          const lastDigit = match ? match[1] : null;
          const positionInCAR = parseInt(lastDigit ?? '', 10);
          if(isNaN(positionInCAR) || positionInCAR < 1 || positionInCAR > 10) {
            console.error('Invalid CAR file name. CAR file name must end with a digit between 1 and 10');
            this.toastrService.error('Invalid CAR file name. CAR file name must end with a digit between 1 and 10');
            addToCarousel = false;
            addToCreatives = false; // Do not add to creatives
            return;
          }
          console.log('CAR file detected:', creativeId, positionInCAR);
          // Check if the carousel object already exists
          if(!this.carousel) {
            // Carousel object does not exist, create a new one
            console.log('Creating new carousel object', creativeId);
            this.carousel = {
              id: creativeId,
              classified: false,
              processIndicator: `0/0`,
              files: []
            };
          } else {
            // Carousel object exists, check if the carousel ID matches to make sure the user is not uploading files from different carousels
            console.log('Carousel object already exists', this.carousel.id);
            if(this.carousel.id !== creativeId) {
              console.error('Carousel ID mismatch. Expected:', this.carousel.id, 'Found:', creativeId);
              this.toastrService.error('Multiple carousel upload is not supported. Please upload files only for ' + this.carousel.id + '.');
              addToCarousel = false;
              addToCreatives = false; // Do not add to creatives
              return;
            }
          }
          // Check if the carousel file already exists
          const existingFile = this.carousel.files.find(file => file.order === positionInCAR);
          if(existingFile) {
            console.error('Carousel file already exists:', creativeId, positionInCAR);
            this.toastrService.error(`Carousel file already exists: ${creativeId} - ${positionInCAR}`);
            addToCarousel = false;
            addToCreatives = false; // Do not add to creatives
            return;
          }
          // Add the file to the carousel object
          if(addToCarousel) {
            this.carousel.files.push({
              id: `${creativeId}_${positionInCAR}`,
              order: positionInCAR,
              status: 'pending',
              file: file
            });
            console.log('Carousel file added:', creativeId, positionInCAR, this.carousel, this.getCarouselStatus(), this.getCarouselFiles());
          }      
          // Update the carousel process indicator
          const totalItems = this.carousel?.files || [];
          const classifiedItems = totalItems.filter(item => item.status === 'done').length;        
          this.carousel!.processIndicator = `${classifiedItems}/${totalItems.length}`;    
        }

        if(addToCreatives && addToCarousel) {
          // Add the file to the creatives array and upload it
          this.initFieldStatusForCreative(creativeId);
          this.creatives.push({
            id: creativeId,
            usps: Array(10).fill('') // Initialize usps as an array of 10 empty strings for new creative
          });
          this.uploadFile(file, index);
        }
      }
    });
  }

  uploadFile(file: File, creativeIndex: number) {
    if (!file) {
      console.error('No file selected for upload.');
      this.toastrService.error('No file selected for upload.');
      return;
    }
    console.log('uploadFile()', file);

    this.creatives[creativeIndex].loading = true;
    this.creatives[creativeIndex].analysing = false;
    this.creatives[creativeIndex].analysisStatus = 'Uploading...';

    const fileOriginalName = file.name;
    const fileType = file.type;
    let creativeId = fileOriginalName.substring(0, 6).toUpperCase();
    if(creativeId.startsWith('CAR')) {
      // If the file is part of a carousel, inject the carousel index in the creativeId
      const match = fileOriginalName.match(/_(\d+)\.[a-z0-9]+$/i); // Extract digit(s) after last underscore before extension
      const lastDigit = match ? match[1] : null;
      if (!lastDigit || isNaN(parseInt(lastDigit, 10)) || parseInt(lastDigit, 10) < 1 || parseInt(lastDigit, 10) > 10) {
        console.error('Invalid CAR file name. CAR file name must end with a digit between 1 and 10');
        this.toastrService.error('Invalid CAR file name. CAR file name must end with a digit between 1 and 10');
        this.creatives[creativeIndex].loading = false;
        this.creatives[creativeIndex].analysisStatus = '';
        return;
      } else {
        creativeId = creativeId + '_' + lastDigit;
      }
    }
    this.creatives[creativeIndex].id = creativeId;

    // If creativeId does not start with IMG, VID or CAR, return error
    if (!creativeId.startsWith('IMG') && !creativeId.startsWith('VID') && !creativeId.startsWith('CAR')) {
      console.error('Invalid file name. Creative ID must start with IMG, VID or CAR');
      this.toastrService.error('Invalid file name. Creative ID must start with IMGXXX, VIDXXX or CARXXX');
      this.creatives[creativeIndex].loading = false;
      this.creatives[creativeIndex].analysisStatus = '';
      return;
    }
    console.log('Uploading file', creativeId, fileOriginalName, fileType);

    // Get S3 temporary user storage Upload URL
    let s3UploadUrl = null;
    let fileTempName = '';
    console.log('Getting S3 Upload URL...', creativeId);
    this.creativeAnalysisService.getS3UploadUrl(fileOriginalName, fileType).then(response => {
      console.log('S3 Upload URL', response);
      s3UploadUrl = response.url;
      fileTempName = response.t_name;
      // Upload file to S3 temporary user storage
      return this.creativeAnalysisService.uploadFileToS3(s3UploadUrl!, file!);
    }).then(() => {
      console.log('File uploaded to S3', creativeId);
      // Import the creative from S3 temporary user storage to Firestore & S3 permanent creative analysis storage
      console.log('Importing creative...', creativeId);
      return this.creativeAnalysisService.importCreative(this.accountId, creativeId, fileOriginalName, fileTempName);
    }).then((importedCreative) => {
      console.log('Creative imported successfully', creativeId, importedCreative);
      this.creatives[creativeIndex].thumbnail = importedCreative.thumbnail;
      this.creatives[creativeIndex].url = importedCreative.url;
      this.creatives[creativeIndex].loading = false;
      this.creatives[creativeIndex].analysisStatus = '';

      // Request creative classification      
      this.classifyCreative(creativeId);

    }).catch(error => {
      console.error('Error getting S3 Upload URL', creativeId, error);
      this.creatives[creativeIndex].loading = false;
      this.creatives[creativeIndex].analysisStatus = '';
    });

  }

  showCreativeDebug(creativeId: string) {
    console.log('showCreativeDebug', creativeId);
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    this.creatives[creativeIndex].showDebug = !this.creatives[creativeIndex].showDebug;
  }

  getCarouselStatus(): string {
    const items = this.carousel?.files || [];
    const done = items.filter(item => item.status === 'done').length;
    return done === items.length ? 'Ready' : `${done}/${items.length} classified`;
  }

  getCarouselFiles(): File[] {
    return this.carousel?.files.map(item => item.file) || [];
  }

  updateCreative(creativeId: string, field: 'creative_style' | 'creative_format' | 'main_angle' | 'target_market' | 'market_awareness' | 'usps' | 'discounts' | 'ugc' | 'headline' | 'person_feature' | 'call_to_action' | 'audio_hook' | 'visual_hook' | 'text_hook' | 'creator' | 'age_group' | 'gender' | 'length' | 'audio_close' | 'visual_close' | 'text_close' | 'number_of_tiles' | 'needs_expert_review' | 'needs_expert_review_reason' | 'needs_qa'): void {
    
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
      console.error('Creative not found in creatives array:', creativeId);
      this.toastrService.error('Creative not found in creatives array');
      return;
    }
    const creative = this.creatives[creativeIndex];

    // Get the value to update
    let updateValue: any;
    switch (field) {
      case 'main_angle':
        updateValue = creative.mainAngle;
        break;
      case 'creative_style':
        updateValue = creative.creativeStyle;
        break;
      case 'creative_format':
        updateValue = creative.creativeFormat;
        break;
      case 'target_market':
        updateValue = creative.targetMarket;
        break;
      case 'market_awareness':
        updateValue = creative.marketAwareness;
        break;
      case 'discounts':
        updateValue = creative.discounts;
        break;
      case 'ugc':
        updateValue = creative.ugc;
        break;
      case 'headline':
        updateValue = creative.headline;
        break;
      case 'call_to_action':
        updateValue = creative.callToAction;
        break;
      case 'person_feature':
        updateValue = creative.personFeature;
        break;
      case 'needs_qa':
        updateValue = creative.needsQa;
        break;
      case 'needs_expert_review':
        updateValue = creative.needsExpertReview;
        break;
      case 'needs_expert_review_reason':
        updateValue = creative.needsExpertReviewReason;
        break;
      case 'audio_hook':
        updateValue = creative.audioHook; 
        break;
      case 'visual_hook':
        updateValue = creative.visualHook;
        break;
      case 'text_hook':
        updateValue = creative.textHook;
        break;
      case 'creator':
        updateValue = creative.creator;
        break;
      case 'age_group':
        updateValue = creative.ageGroup;
        break;
      case 'gender':
        updateValue = creative.gender;
        break;
      case 'length':
        updateValue = creative.length;
        break;
      case 'audio_close':
        updateValue = creative.audioClose;
        break;
      case 'visual_close':
        updateValue = creative.visualClose;
        break;
      case 'text_close':
        updateValue = creative.textClose;
        break;
      case 'number_of_tiles':
        updateValue = creative.numberOfTiles;
        break;
      case 'usps':
        updateValue = (creative.usps ?? []).filter( // Filter out empty strings before sending
          (usp: string) => usp && usp.trim().length > 0 //
        );
        break;
    }
  
    console.log('Updating creative field', creativeId, field, updateValue);

    if(field === 'creative_style') {
      if (updateValue && creative.creativeStyle?.includes(updateValue) === false) {
        console.error('Selected style is not in the list of creative styles');
        this.toastrService.error('Selected style must be in the list of Creative Styles');
        return;
      }
    }

    if(field === 'creative_format') {
      if (updateValue && creative.creativeFormat?.includes(updateValue) === false) {
        console.error('Selected format is not in the list of creative format');
        this.toastrService.error('Selected format must be in the list of Creative Format');
        return;
      }
    }

    if(field === 'person_feature') {
      if (updateValue && creative.personFeature?.includes(updateValue) === false) {
        console.error('Selected value is not in the list of person features');
        this.toastrService.error('Selected value must be in the list of Person Features');
        return;
      }
    }

    if(field === 'ugc') {
      if (updateValue && creative.ugc?.includes(updateValue) === false) {
        console.error('Selected value is not in the list of UGC');
        this.toastrService.error('Selected value must be in the list of UGC');
        return;
      }
    }

    if(field === 'market_awareness') {
      if (updateValue && creative.marketAwareness?.includes(updateValue) === false) {
        console.error('Selected value is not in the list of market awareness');
        this.toastrService.error('Selected value must be in the list of Market Awareness');
        return;
      }
    }

    if(field === 'gender') {
      if (updateValue && creative.gender?.includes(updateValue) === false) {
        console.error('Selected value is not in the list of gender');
        this.toastrService.error('Selected value must be in the list of gender');
        return;
      }
    }

    if(field === 'needs_expert_review') {
      if (updateValue && _.isBoolean(updateValue) === false) {
        console.error('Selected value for needs_expert_review is not a boolean');
        this.toastrService.error('Selected value for Needs Review must be a boolean');
        return;        
      }
    }

    if(field === 'needs_qa') {
      if (updateValue && _.isBoolean(updateValue) === false) {
        console.error('Selected value for needs_qa is not a boolean');
        this.toastrService.error('Selected value for Needs QA must be a boolean');
        return;        
      }
    }
  
    if(updateValue !== undefined) {
      const data: Record<string, any> = {};
      data[field] = updateValue;
      console.log('Data to send:', data);
  
      this.updateFieldValue[creativeId][field].showButton = false;
      this.updateFieldValue[creativeId][field].showLoading = true;
  
      this.creativeAnalysisService.updateCreative(this.accountId, creativeId, data)
        .then(() => {
          console.log('Creative updated successfully');
          this.updateFieldValue[creativeId][field].showButton = true;
          this.updateFieldValue[creativeId][field].showLoading = false;
          this.updateFieldValue[creativeId][field].updateStatus = 'saved';
          this.toastrService.success('Creative updated successfully');
        })
        .catch(error => {
          console.error('Error updating creative', error);
          this.updateFieldValue[creativeId][field].showButton = true;
          this.updateFieldValue[creativeId][field].showLoading = false;
          this.updateFieldValue[creativeId][field].updateStatus = 'error';
          this.toastrService.error('Error updating creative');
        });
    } else {
      console.error('No value to update');
      this.toastrService.error('No value to update');
    }
  }

  getCreativeStyles() {
    console.log('Loading creative styles...');
    this.creativeAnalysisService.listCreativeStyles(this.accountId).then(creativeStyles => {
      let styles: CreativeStyle[] = [];
      creativeStyles.forEach((style: any) => {
        styles.push(style);
      });
      this.creativeStyles = styles;
      console.log("Loaded Creative Styles", this.creativeStyles);
    }).catch(error => {
      console.error('Error fetching creative styles', error);
      this.toastrService.error('Error fetching creative styles');
    });
  }

  buildSelectCreativeStyles(creativeId: string, selectedStyle: string): void {
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
       console.error('Creative not found in creatives array:', creativeId);
       this.toastrService.error('Creative not found in creatives array');
       return;
    }
    const creative = this.creatives[creativeIndex];

    this.selectCreativeStyles[creativeId] = [];
    let topStyles: string[] = [];
    // Add the selected style at the start of the top styles
    if (selectedStyle) {
      topStyles.push(selectedStyle);
    }
    this.creativeStyles.forEach((style: CreativeStyle) => {
      if((creative.creativeStyleAlternatives ?? []).includes(style.name) === false && style.name !== selectedStyle) {
        // Remove the top styles and the selected style from the main array
        this.selectCreativeStyles[creativeId].push(style.name);
      } else {
        topStyles.push(style.name);
      }
    });
    // Add the top styles at the beginning of the array
    // Sort the top styles alphabetically from Z to A
    topStyles.sort((a: string, b: string) => b.localeCompare(a));
    // Remove duplicates from topStyles
    topStyles = [...new Set(topStyles)];
    // Add a --- separator
    this.selectCreativeStyles[creativeId].unshift('---');
    topStyles.forEach((style: string) => {
      this.selectCreativeStyles[creativeId].unshift(style); // Add the selected style at the start
    });

    // console.log('selectCreativeStyles', this.selectCreativeStyles[creativeId]);
  }

  buildSelectCreativeFormat(creativeId: string) {
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
       console.error('Creative not found in creatives array:', creativeId);
       this.toastrService.error('Creative not found in creatives array');
       return;
    }

    this.selectCreativeFormat[creativeId] = [];
    this.creativeFormats.forEach((format: CreativeFormat) => {
      if(creativeId.substring(0, 3).toUpperCase() == 'IMG' && format.forImage === true) this.selectCreativeFormat[creativeId].push(format.name);
      if((creativeId.substring(0, 3).toUpperCase() == 'VID' || creativeId.substring(0, 3).toUpperCase() == 'CAR') && format.forVideo === true) this.selectCreativeFormat[creativeId].push(format.name);
    });

    // console.log('selectCreativeFormat', this.selectCreativeFormat[creativeId]);
  }

  buildSelectPersonFeature(creativeId: string): void {
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
       console.error('Creative not found in creatives array:', creativeId);
       this.toastrService.error('Creative not found in creatives array');
       return;
    }

    this.selectPersonFeature[creativeId] = [];
    this.personFeatures.forEach((feature: string) => {
      this.selectPersonFeature[creativeId].push(feature);
    });

    // console.log('selectPersonFeature', this.selectPersonFeature[creativeId]);
  }

  buildSelectUgc(creativeId: string): void {
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
       console.error('Creative not found in creatives array:', creativeId);
       this.toastrService.error('Creative not found in creatives array');
       return;
    }

    this.selectUgc[creativeId] = [];
    this.ugc.forEach((feature: string) => {
      this.selectUgc[creativeId].push(feature);
    });

    // console.log('selectUgc', this.selectUgc[creativeId]);
  }

  buildSelectMarketAwareness(creativeId: string): void {
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
       console.error('Creative not found in creatives array:', creativeId);
       this.toastrService.error('Creative not found in creatives array');
       return;
    }

    this.selectMarketAwareness[creativeId] = [];
    this.marketAwareness.forEach((feature: string) => {
      this.selectMarketAwareness[creativeId].push(feature);
    });

    // console.log('selectMarketAwareness', this.selectMarketAwareness[creativeId]);
  }

  buildSelectGender(creativeId: string): void {
    // Get the creative from the creatives array
    const creativeIndex = this.creatives.findIndex(creative => creative.id === creativeId);
    if (creativeIndex === -1) {
       console.error('Creative not found in creatives array:', creativeId);
       this.toastrService.error('Creative not found in creatives array');
       return;
    }

    this.selectGender[creativeId] = [];
    this.gender.forEach((feature: string) => {
      this.selectGender[creativeId].push(feature);
    }
    );
    // console.log('selectGender', this.selectGender[creativeId]);
  }


  onAsyncDrop(event: DragEvent): void {
    console.log('onAsyncDrop', event);
    event.preventDefault();
    event.stopPropagation();
    if(event.dataTransfer) {
      const files = event.dataTransfer.files;
      console.log('onAsyncDrop files:', files);
      if(files.length > this.maxFilesAsyncDrop) {
        this.toastrService.error(`Maximum ${this.maxFilesAsyncDrop} files allowed in a async batch`);
        return;
      }
      this.processAsyncDroppedFiles(files);
    } else {
      this.toastrService.error('No dropped files');
    }    
  }

  onAsyncDragOver(event: DragEvent): void {
    // console.log('onAsyncDragOver', event);
    event.preventDefault();
    event.stopPropagation();
  }

  onAsyncDragLeave(event: DragEvent): void {
    // console.log('onAsyncDragLeave', event);
    event.preventDefault();
    event.stopPropagation();
  }

  processAsyncDroppedFiles(files: FileList): void {
    if(!files || files.length === 0) {
      console.error('No files selected for upload');
      this.toastrService.error('No files selected for upload.');
      return;
    }
    if(files.length > this.maxFilesAsyncDrop) {
      this.toastrService.error(`Maximum ${this.maxFilesAsyncDrop} files allowed in an async batch`);
      return;
    }
    const fileArray = Array.from(files);
    fileArray.forEach((file, index) => {
      if(file) {
        const creativeId = file.name.substring(0, 6).toUpperCase();
        console.log('processAsyncDroppedFiles:', creativeId, file);
        this.asyncDropFiles.push({name: file.name, file: file, status: 'pending'})        
      }
    });

    if(this.asyncDropFiles.length > 0) {
      this.asyncDropFiles.forEach((item, index) => {
        this.showAsyncDrop = false;
        this.uploadAsyncFile(item.file, index);
      });
    } else {
      console.error('No files to upload');
      this.toastrService.error('No files to upload.');
      return;
    }
  }

  uploadAsyncFile(file: File, fileIndex: number) {
    if (!file) {
      console.error('No file selected for upload.');
      this.toastrService.error('No file selected for upload.');
      return;
    }
    console.log('uploadAsyncFile()', file);

    this.asyncDropFiles[fileIndex].status = 'uploading...';

    const fileOriginalName = file.name;
    const fileType = file.type;
    let creativeId = fileOriginalName.substring(0, 6).toUpperCase();
    if(creativeId.startsWith('CAR')) {
      // If the file is part of a carousel, inject the carousel index in the creativeId
      const match = fileOriginalName.match(/_(\d+)\.[a-z0-9]+$/i); // Extract digit(s) after last underscore before extension
      const lastDigit = match ? match[1] : null;
      if (!lastDigit || isNaN(parseInt(lastDigit, 10)) || parseInt(lastDigit, 10) < 1 || parseInt(lastDigit, 10) > 10) {
        console.error('Invalid CAR file name. CAR file name must end with a digit between 1 and 10');
        this.toastrService.error('Invalid CAR file name. CAR file name must end with a digit between 1 and 10');
        this.asyncDropFiles[fileIndex].status = 'error';
        return;
      } else {
        creativeId = creativeId + '_' + lastDigit;
      }
    }
    // this.creatives[fileIndex].id = creativeId;

    // If creativeId does not start with IMG, VID or CAR, return error
    if (!creativeId.startsWith('IMG') && !creativeId.startsWith('VID') && !creativeId.startsWith('CAR')) {
      console.error('Invalid file name. Creative ID must start with IMG, VID or CAR');
      this.toastrService.error('Invalid file name. Creative ID must start with IMGXXX, VIDXXX or CARXXX');
      this.asyncDropFiles[fileIndex].status = 'error';
      return;
    }
    console.log('Uploading file', creativeId, fileOriginalName, fileType);

    // Get S3 temporary user storage Upload URL
    let s3UploadUrl = null;
    let fileTempName = '';
    console.log('Getting S3 Upload URL...', creativeId);
    this.creativeAnalysisService.getS3UploadUrl(fileOriginalName, fileType).then(response => {
      console.log('S3 Upload URL', response);
      s3UploadUrl = response.url;
      fileTempName = response.t_name;
      // Upload file to S3 temporary user storage
      return this.creativeAnalysisService.uploadFileToS3(s3UploadUrl!, file!);
    }).then(() => {
      console.log('File uploaded to S3', creativeId);
      // Import the creative from S3 temporary user storage to Firestore & S3 permanent creative analysis storage
      console.log('Importing creative...', creativeId);
      return this.creativeAnalysisService.importCreative(this.accountId, creativeId, fileOriginalName, fileTempName);
    }).then((importedCreative) => {
      console.log('Creative imported successfully', creativeId, importedCreative);
      this.asyncDropFiles[fileIndex].status = 'uploaded';

    }).catch(error => {
      console.error('Error getting S3 Upload URL', creativeId, error);
      this.asyncDropFiles[fileIndex].status = 'error';
    });

  }

  reviewCreatives(type: 'img' | 'vid' | 'car') {
    this.reviewFormat = type;
    this.pageTitle = `Review ${type.toUpperCase()}s`;
    this.showUploadCreatives = false;
    this.loadingCreatives = true;
    this.reviewShowNextPageLink = false;
    this.creativeAnalysisService.reviewCreatives(this.accountId, type, 100, this.reviewNextPageToken)
    .then(reviewCreativesResponse => {
      console.log('reviewCreatives:', reviewCreativesResponse);
      this.reviewNextPageToken = reviewCreativesResponse.nextPageToken;
      this.loadingCreatives = false;
      this.reviewShowNextPageLink = true;
      if(reviewCreativesResponse && reviewCreativesResponse.results && reviewCreativesResponse.results.length > 0) {
        reviewCreativesResponse.results.forEach((creative: any, index: number) => {
          console.log('[reviewCreatives] Loaded creative:', index, creative);

          let uspsForThisCreative: string[] = [];
          if (Array.isArray(creative.usps)) {
            uspsForThisCreative = [...creative.usps]; // Create a new array
          }
          while ((uspsForThisCreative?.length ?? 0) < 10) {
            uspsForThisCreative.push('');
          }

          const thisCreative = {
            id: creative.id,
            created: creative.created,
            file: creative.file,
            thumbnail: creative.thumbnail,
            status: creative.status,
            classified: creative.classified,
            url: creative.url,
            mainAngle: creative.main_angle,
            targetMarket: creative.target_market,
            creativeStyle: creative.creative_style,
            creativeFormat: creative.creative_format,
            marketAwareness: creative.market_awareness,
            discounts: creative.discounts,
            needsQa: creative.needs_qa,
            needsExpertReview: creative.needs_expert_review,
            needsExpertReviewReason: creative.needs_expert_review_reason,
            callToAction: creative.call_to_action,
            headline: creative.headline,
            personFeature: creative.person_feature,
            creator: creative.creator,
            ageGroup: creative.age_group,
            audioClose: creative.audio_close,
            audioHook: creative.audio_hook,
            gender: creative.gender,
            length: creative.length,
            textClose: creative.text_close,
            textHook: creative.text_hook,
            ugc: creative.ugc,
            visualClose: creative.visual_close,
            visualHook: creative.visual_hook,
            numberOfTiles: creative.number_of_tiles,
            // Ensure usps is an array and pad it
            usps: Array.isArray(creative.usps) ? creative.usps : []
          };
          // Pad the usps array for the new creative
          while ((thisCreative.usps?.length ?? 0) < 10) { //
            thisCreative.usps?.push(''); //
          }

          this.initFieldStatusForCreative(creative.id);
          this.creatives.push(thisCreative);
          // console.log('Added creative to creatives:', thisCreative);
          // Build the select lists
          this.buildSelectCreativeStyles(creative.id, this.creatives[index].creativeStyle || '');
          this.buildSelectCreativeFormat(creative.id);
          this.buildSelectPersonFeature(creative.id);
          this.buildSelectUgc(creative.id);
          this.buildSelectMarketAwareness(creative.id);
          this.buildSelectGender(creative.id);

          // Get the carousel creatives
          if(type === 'car' && creative.id.length == 6) {            
            this.getCarouselCreative(creative.id);
          }
        });   

      } else {
        console.log('No creatives found');
        this.toastrService.error('No creatives found');
      }     
    })
    .catch(error => {
      console.log('reviewCreatives error', error);
    });

  }

  initFieldStatusForCreative(creativeId: string) {
    const fields = [
      'creative_style', 'creative_format', 'main_angle', 'target_market', 'market_awareness',
      'usps', 'discounts', 'ugc', 'headline', 'person_feature', 'call_to_action', 'audio_hook',
      'visual_hook', 'text_hook', 'creator', 'age_group', 'gender', 'length', 'audio_close',
      'visual_close', 'text_close', 'number_of_tiles', 'needs_qa',
      'needs_expert_review', 'needs_expert_review_reason'
    ];

    this.updateFieldValue[creativeId] = {};
    for (const field of fields) {
      this.updateFieldValue[creativeId][field] = {
        name: field,
        showButton: true,
        showLoading: false,
        updateStatus: null
      };
    }
  }

  getCarouselCreative(creativeId: string) {
    // Get the carousel creatives
    this.creativeAnalysisService.getCarouselCreatives(this.accountId, creativeId)
    .then(carouselCreatives => {
      console.log("[getCarouselCreatives] Carousel creatives", creativeId, carouselCreatives);
      let creatives: CarouselCreative[] = [];
      if(carouselCreatives && carouselCreatives.length > 0) {
        _.forEach(carouselCreatives, (creative => {
          creatives.push({
            id: creative.id,
            thumbnail: creative.thumbnail,
            url: creative.url,
            fileName: creative.fileName
          })
        }))
      }
      this.carouselsCreatives[creativeId] = creatives;
      console.log("this.carouselCreatives", creativeId, this.carouselsCreatives[creativeId]);
    })
    .catch(error => {
      console.error('[getCarouselCreatives] Error fetching carousel creatives', error);
    });
  }

}
